import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  QrCode, User, LogOut, Plus, Wrench, ChevronRight, Settings, ScanLine,
  CheckCircle, Clock, XCircle
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getIdToken } from "../lib/firebase";

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  working: { icon: CheckCircle, color: "text-[#16A34A]", bg: "bg-[#16A34A]/10", label: "Working" },
  under_maintenance: { icon: Clock, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "Under Maintenance" },
  not_working: { icon: XCircle, color: "text-[#DC2626]", bg: "bg-[#DC2626]/10", label: "Not Working" },
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, userProfile, logout, isAdmin } = useAuth();
  const [qrCodes, setQRCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQRCodes();
  }, [user]);

  const fetchQRCodes = async () => {
    if (!user) return;
    
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/my-qrs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQRCodes(data);
      }
    } catch (error) {
      console.error("Fetch QR codes error:", error);
      toast.error("Failed to load QR codes");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Logout failed");
    }
  };

  const handleScan = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#64748B] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#64748B]/10 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-[#64748B]" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Welcome back</p>
              <p className="font-semibold text-[#1F2933]" style={{ fontFamily: 'Manrope' }}>
                {userProfile?.name || user?.email || "User"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                data-testid="admin-panel-btn"
                className="text-[#64748B]"
              >
                <Settings className="w-5 h-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-[#DC2626]"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#64748B]" style={{ fontFamily: 'Manrope' }}>
                {qrCodes.length}
              </p>
              <p className="text-xs text-[#6B7280]">Machines</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#16A34A]" style={{ fontFamily: 'Manrope' }}>
                {qrCodes.filter(q => q.data?.current_status === "working").length}
              </p>
              <p className="text-xs text-[#6B7280]">Working</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#F59E0B]" style={{ fontFamily: 'Manrope' }}>
                {qrCodes.filter(q => q.data?.current_status === "under_maintenance" || q.data?.current_status === "not_working").length}
              </p>
              <p className="text-xs text-[#6B7280]">Attention</p>
            </CardContent>
          </Card>
        </div>

        {/* Machines List */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1F2933]" style={{ fontFamily: 'Manrope' }}>
              My Machines
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleScan}
              className="text-[#64748B]"
              data-testid="scan-new-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add New
            </Button>
          </div>

          {qrCodes.length === 0 ? (
            <Card className="bg-white shadow-sm">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#F8FAFC] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-[#9CA3AF]" />
                </div>
                <h3 className="font-semibold text-[#1F2933] mb-2" style={{ fontFamily: 'Manrope' }}>
                  No Machines Yet
                </h3>
                <p className="text-sm text-[#6B7280] mb-4">
                  Scan a QR code to assign it to a machine and start tracking maintenance.
                </p>
                <Button onClick={handleScan} className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="scan-first-btn">
                  <ScanLine className="w-4 h-4 mr-2" />
                  Scan QR Code
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {qrCodes.map((qr) => {
                const machineStatus = qr.data?.current_status || "working";
                const statusInfo = statusConfig[machineStatus] || statusConfig.working;
                const StatusIcon = statusInfo.icon;
                const logsCount = qr.data?.maintenance_logs?.length || 0;
                
                return (
                  <Card 
                    key={qr.qr_id} 
                    className="bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/edit/${qr.code}`)}
                    data-testid={`qr-card-${qr.code}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusInfo.bg}`}>
                          <Wrench className={`w-6 h-6 ${statusInfo.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-[#1F2933] truncate">
                              {qr.data?.machine_name || "Unnamed Machine"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                            <span className="font-mono">{qr.code}</span>
                            {qr.data?.machine_id && (
                              <>
                                <span>•</span>
                                <span>{qr.data.machine_id}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge className={`${statusInfo.bg} ${statusInfo.color} border-0 text-xs`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                          {logsCount > 0 && (
                            <span className="text-xs text-[#9CA3AF]">{logsCount} logs</span>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#9CA3AF]" />
                      </div>
                      {/* Location and Next Service */}
                      {(qr.data?.location || qr.data?.next_service_due) && (
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-[#6B7280]">
                          {qr.data?.location && (
                            <span className="truncate">📍 {qr.data.location}</span>
                          )}
                          {qr.data?.next_service_due && (
                            <span>📅 Next: {qr.data.next_service_due}</span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] z-50">
        <div className="flex items-center justify-around h-16 max-w-md mx-auto">
          <button
            onClick={handleScan}
            className="flex flex-col items-center gap-1 text-[#9CA3AF]"
            data-testid="nav-scan"
          >
            <ScanLine className="w-6 h-6" />
            <span className="text-xs">Scan</span>
          </button>
          <button
            className="flex flex-col items-center gap-1 text-[#64748B]"
            data-testid="nav-dashboard"
          >
            <Wrench className="w-6 h-6" />
            <span className="text-xs font-medium">Machines</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex flex-col items-center gap-1 text-[#9CA3AF]"
            data-testid="nav-logout"
          >
            <LogOut className="w-6 h-6" />
            <span className="text-xs">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
